import { Button } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AddIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  Table,
  TableHeader,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { toastr } from 'react-redux-toastr';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { sortStrings } from '@fortress-technology-solutions/fortress-component-library/utils';
import TableLayoutV2 from '../../../components/TableLayoutV2';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import PropertyTransactionCodesService from '../../../services/propertyTransactionCodesService';
import {
  createHudVoucherSpecialClaim,
  getHudVoucherSpecialClaims,
  updateHudVoucherSpecialClaimApprovedAmount,
} from '../../../services/hudVouchersService';
import AddSpecialClaimModal from './AddSpecialClaimModal';
import { getClaimTypeLabel, getSpecialClaimStatusLabel } from './constants';
import { useSpecialClaimsTable } from './hooks';
import messages from './messages';
import parentMessages from '../messages';
import DeleteSpecialClaimModal from './DeleteSpecialClaimModal';
import HudVoucherService from '../../../services/hudVoucherService';
import useAbortController from '../../../hooks/useAbortController';
import ConditionalTooltip from '../../../components/ConditionalTooltip';
import { BASE_TABLE_LAYOUT_SX } from '../constants';

// API duplicate check vs DB unique (e.g. read replica lag) — both surface as these substrings.
const DUPLICATE_HUD_CLAIM_ID_PATTERN =
  /hudclaimid.+already exists|hudvoucherspecialclaim.+validation error/i;

const createSpecialClaimErrorMessage = (error: any, intl: any): string => {
  const fallback = intl.formatMessage(messages.createError);
  if (error && typeof error === 'object' && error.networkError) {
    return fallback;
  }
  let raw = '';
  if (typeof error === 'string') {
    raw = error.trim();
  } else if (typeof error?.message === 'string') {
    raw = error.message.trim();
  }
  if (raw.length === 0) {
    return fallback;
  }
  if (DUPLICATE_HUD_CLAIM_ID_PATTERN.test(raw)) {
    return intl.formatMessage(messages.createDuplicateHudClaimIdError);
  }
  return fallback;
};

interface Props {
  show: boolean;
  intl: any;
  organizationId?: string;
  propertyId?: string;
  voucherId?: string;
  voucherSubmissionStatus?: string;
  onVoucherStatusChange?: (newStatus: string) => void;
  onRefreshVoucher?: () => void;
  isReadOnly?: boolean;
  hasVariance?: boolean;
  canManage: boolean;
}

function SpecialClaims({
  show,
  intl,
  organizationId,
  propertyId,
  voucherId,
  voucherSubmissionStatus,
  onVoucherStatusChange,
  onRefreshVoucher,
  isReadOnly = false,
  hasVariance = false,
  canManage,
}: Props) {
  const queryClient = useQueryClient();
  const selectedProperty = useSelectedProperty();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const abortControllerOptions = useAbortController();

  const hudVoucherService = useMemo(() => new HudVoucherService(), []);

  const specialClaimsQueryKey = useMemo(
    () => ['hudVoucherSpecialClaims', organizationId, propertyId, voucherId],
    [organizationId, propertyId, voucherId],
  );

  const voucherStatsQueryKey = useMemo(
    () => ['hudVoucherStats', organizationId, propertyId, voucherId],
    [organizationId, propertyId, voucherId],
  );

  const isQueryEnabled = Boolean(
    show && organizationId && propertyId && voucherId,
  );

  const isChargeCodesQueryEnabled = Boolean(
    show && organizationId && propertyId,
  );
  const { data: propertyTransactionCodes = [] } = useQuery(
    ['propertyTransactionCodes', organizationId, propertyId],
    async () => {
      const service = new PropertyTransactionCodesService();
      return service.getPropertyTransactionCodes(organizationId, propertyId);
    },
    {
      enabled: isChargeCodesQueryEnabled,
      staleTime: 5 * 60 * 1000,
      onError: () => {
        toastr.error(
          'Error',
          intl.formatMessage(messages.loadChargeCodesError),
        );
      },
    },
  );

  const { data: specialClaims = [], isLoading: isSpecialClaimsLoading } =
    useQuery(
      specialClaimsQueryKey,
      () =>
        getHudVoucherSpecialClaims({
          organizationId,
          propertyId,
          voucherId,
        }),
      {
        enabled: isQueryEnabled,
        onError: () => {
          toastr.error('Error', intl.formatMessage(messages.loadError));
        },
      },
    );

  const { mutateAsync: createSpecialClaim, isLoading: isCreating } =
    useMutation(
      ['createHudVoucherSpecialClaim', organizationId, propertyId, voucherId],
      (payload) =>
        createHudVoucherSpecialClaim({
          organizationId,
          propertyId,
          voucherId,
          payload,
        }),
      {
        onSuccess: async () => {
          const previousStatus = voucherSubmissionStatus;
          toastr.success('Success', intl.formatMessage(messages.createSuccess));

          if (onRefreshVoucher) {
            await onRefreshVoucher();
          }

          if (previousStatus === 'Submitted' && onVoucherStatusChange) {
            onVoucherStatusChange('Draft');
            toastr.warning(
              intl.formatMessage(messages.voucherStatusUpdatedTitle),
              intl.formatMessage(messages.voucherReturnedToDraftWarning),
            );
          }
        },
        onError: (error) => {
          toastr.error('Error', createSpecialClaimErrorMessage(error, intl));
        },
      },
    );

  const deleteSpecialClaimMutation = useMutation(
    (specialClaimId: string) =>
      hudVoucherService.deleteSpecialClaim(
        organizationId,
        propertyId,
        voucherId,
        specialClaimId,
        abortControllerOptions,
      ),
    {
      onSuccess: async (data) => {
        setDeleteModalOpen(false);
        setSelectedClaim(null);

        // Check if voucher status was updated to Draft
        const previousStatus = voucherSubmissionStatus;

        toastr.success('Success', 'Special claim deleted successfully');

        // Refresh voucher data to get latest status from backend
        if (onRefreshVoucher) {
          await onRefreshVoucher();
        }

        // If voucher was submitted and is now Draft, notify user
        if (previousStatus === 'Submitted' && onVoucherStatusChange) {
          onVoucherStatusChange('Draft');
          toastr.warning(
            'Voucher Status Updated',
            'The voucher has been returned to Draft status. A new submission will be required.',
          );
        }
      },
      onError: (error: any) => {
        let errorMessage = 'Failed to delete special claim. Please try again.';

        if (error?.message) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        } else if (error?.error) {
          errorMessage = error.error;
        }

        // Handle specific error codes
        if (errorMessage.includes('400') || errorMessage.includes('paid')) {
          errorMessage = intl.formatMessage(messages.deletePaidError);
        } else if (errorMessage.includes('404')) {
          errorMessage = intl.formatMessage(messages.deleteNotFoundError);
        } else if (errorMessage.includes('403')) {
          errorMessage = intl.formatMessage(
            messages.deleteFeatureUnavailableError,
          );
        }

        toastr.error('Error', errorMessage);
      },
    },
  );

  const transactionCodes = useMemo(() => {
    return (
      (Array.isArray(propertyTransactionCodes)
        ? propertyTransactionCodes
        : propertyTransactionCodes?.codes) ??
      selectedProperty?.transactionCodes?.codes ??
      []
    );
  }, [propertyTransactionCodes, selectedProperty]);

  const chargeCodeOptions = useMemo(() => {
    return transactionCodes
      .filter((code) => {
        const tc = code?.transactionCode;
        return (
          code?.userSelectable === true &&
          tc?.category === 'Misc' &&
          tc?.transactionType?.name === 'Misc Charge'
        );
      })
      .map((code) => {
        const transactionCode = code?.transactionCode?.code ?? '';
        const description = code?.transactionCode?.description ?? '';
        return {
          value: code.id,
          text: `${transactionCode} - ${description}`.trim(),
        };
      })
      .filter((opt) => opt.value && opt.text)
      .sort(sortStrings('text', 'ASC'));
  }, [transactionCodes]);

  const propertyTransactionCodeById = useMemo(() => {
    return (transactionCodes || []).reduce((acc, code) => {
      const id = code?.id;
      const transactionCode = code?.transactionCode?.code;
      if (id && transactionCode) {
        acc[id] = transactionCode;
      }
      return acc;
    }, {});
  }, [transactionCodes]);

  const specialClaimsForTable = useMemo(() => {
    return (specialClaims || []).map((row) => {
      const ptcId = row?.propertyTransactionCodeId;
      return {
        ...row,
        statusDisplay: getSpecialClaimStatusLabel(row?.status),
        claimTypeDisplay: getClaimTypeLabel(row?.claimType),
        propertyTransactionCodeDisplay:
          (ptcId && propertyTransactionCodeById[ptcId]) || ptcId,
      };
    });
  }, [specialClaims, propertyTransactionCodeById]);

  const handleAddNew = useCallback(() => {
    setIsAddModalOpen(true);
  }, []);

  const handleCloseAddModal = useCallback(() => {
    setIsAddModalOpen(false);
  }, []);

  const handleDeleteClick = useCallback((claim) => {
    setSelectedClaim(claim);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    // Backend expects specialClaimId (GUID), try specialClaimId first, then id
    const specialClaimId = selectedClaim?.specialClaimId || selectedClaim?.id;
    if (specialClaimId) {
      deleteSpecialClaimMutation.mutate(specialClaimId);
    } else {
      toastr.error('Error', intl.formatMessage(messages.deleteMissingIdError));
    }
  }, [selectedClaim, deleteSpecialClaimMutation, intl]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteModalOpen(false);
    setSelectedClaim(null);
  }, []);

  const handleSubmitNewSpecialClaim = useCallback(
    async (payload) => {
      await createSpecialClaim(payload);
      setIsAddModalOpen(false);
    },
    [createSpecialClaim],
  );

  const { mutateAsync: updateSpecialClaimApprovedAmount } = useMutation(
    ({ specialClaimId, approvedAmount }) =>
      updateHudVoucherSpecialClaimApprovedAmount({
        organizationId,
        propertyId,
        voucherId,
        specialClaimId,
        approvedAmount,
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(specialClaimsQueryKey);
        await queryClient.invalidateQueries(voucherStatsQueryKey);
        toastr.success(
          'Success',
          intl.formatMessage(messages.updateApprovedAmountSuccess),
        );
      },
      onError: () => {
        toastr.error(
          'Error',
          intl.formatMessage(messages.updateApprovedAmountError),
        );
      },
    },
  );

  const handleSaveApprovedAmount = useCallback(
    (specialClaimRow, approvedAmount) =>
      updateSpecialClaimApprovedAmount({
        specialClaimId: specialClaimRow.id,
        approvedAmount,
      }),
    [updateSpecialClaimApprovedAmount],
  );

  const {
    baseTableProps,
    name,
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
    filterState,
    filterTypeState,
    dateState,
    searchState,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    isLoading,
    order,
    orderBy,
    rows,
    count,
    totalCount,
  } = useSpecialClaimsTable({
    specialClaims: specialClaimsForTable,
    isReadOnly,
    onDelete: isReadOnly ? undefined : handleDeleteClick,
    voucherSubmissionStatus,
    hasVariance,
    onSaveApprovedAmount: handleSaveApprovedAmount,
    canManage,
  });

  if (!show) {
    return null;
  }

  return (
    <>
      <TableLayoutV2 name={name} fullPage sx={BASE_TABLE_LAYOUT_SX}>
        {(height) => (
          <>
            <TableHeader
              title={<FormattedMessage {...messages.tableTitle} />}
              count={count}
              totalCount={totalCount}
              actions={
                <ConditionalTooltip
                  show={!canManage}
                  title={intl.formatMessage(parentMessages.noPermissionTooltip)}
                  placement="top"
                >
                  <Button
                    startIcon={<AddIcon />}
                    variant="shout"
                    onClick={handleAddNew}
                    disabled={!isQueryEnabled || isReadOnly || !canManage}
                  >
                    {intl.formatMessage(messages.addNew)}
                  </Button>
                </ConditionalTooltip>
              }
            />
            <Table
              {...baseTableProps}
              name={name}
              allColumnsHidden={allColumnsHidden}
              columnOptions={columnOptions}
              selectedColumns={selectedColumns}
              handleColumnChange={handleColumnChange}
              dateState={dateState}
              filterState={filterState}
              filterTypeState={filterTypeState}
              searchState={searchState}
              handleSortChange={handleSortChange}
              handleFilterChange={handleFilterChange}
              handleFilterTypeChange={handleFilterTypeChange}
              handleSearchSubmit={handleSearchSubmit}
              handleDateSubmit={handleDateSubmit}
              headers={filteredHeaders}
              intl={intl}
              isLoading={isLoading || isSpecialClaimsLoading}
              order={order}
              orderBy={orderBy}
              rows={rows}
              count={count}
              height={height}
              emptyStateMessage={baseTableProps.intl.formatMessage(
                messages.emptyStateMessage,
              )}
              totalCount={totalCount}
            />
          </>
        )}
      </TableLayoutV2>

      <AddSpecialClaimModal
        open={isAddModalOpen}
        intl={intl}
        chargeCodeOptions={chargeCodeOptions}
        isSubmitting={isCreating}
        onClose={handleCloseAddModal}
        onSubmit={handleSubmitNewSpecialClaim}
      />

      <DeleteSpecialClaimModal
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        intl={intl}
        isDeleting={deleteSpecialClaimMutation.isLoading}
      />
    </>
  );
}

export default injectIntl(SpecialClaims);
